import { flatMap } from 'lodash';
import { defineStore } from 'pinia';
import { ref } from 'vue';

import { crossbeamApi } from '@/api';
import { CROSSBEAM_DEFAULT_LABEL } from '@/constants/data_shares';
import { initStore } from '@/stores/store-utils';
import {
  DataSharePreset,
  PresetOptionsGroup,
} from '@/types/data_share_presets';

export const useDataSharePresetsStore = defineStore(
  'DataSharePresetsStore',
  () => {
    const allPresets = ref<DataSharePreset[]>([]);

    const { ready, readySync, refresh } = initStore(async () => {
      const { data, error } = await crossbeamApi.GET(
        '/v0.1/data-share-presets',
      );
      if (error) throw error;
      if (data) allPresets.value = data;
    });

    function getFilteredPresetsByFeeId(feedId: number, baseSourceId?: number) {
      // CRM presets
      const presetsByFeedId =
        allPresets.value?.filter((item) => item.feed_id === feedId) || [];

      // Gsheet or Csv presets
      const presetsByBaseSourceId =
        presetsByFeedId.filter(
          (item) => item.base_source_id === baseSourceId,
        ) || [];

      return baseSourceId ? presetsByBaseSourceId : presetsByFeedId;
    }

    function getDefaultPresetId(feedId: number, baseSourceId?: number) {
      const filteredPresets = getFilteredPresetsByFeeId(feedId, baseSourceId);
      return filteredPresets.find(
        (preset) => preset.preset_type === 'crossbeam_default',
      )?.id;
    }

    function getPresetName(
      feedId: number,
      baseSourceId?: number,
      presetId?: number,
      isNew?: boolean,
    ) {
      const filteredPresets = getFilteredPresetsByFeeId(feedId, baseSourceId);

      return (
        filteredPresets.find((item) => item.id === presetId)?.name ||
        (isNew ? '' : CROSSBEAM_DEFAULT_LABEL)
      );
    }

    function getSourceFieldIdsByPresetId(
      feedId: number,
      baseSourceId?: number,
      id?: number,
    ) {
      const filteredPresets = getFilteredPresetsByFeeId(feedId, baseSourceId);
      const preset = filteredPresets.filter(
        (preset: DataSharePreset) => preset.id === id,
      );
      return flatMap(preset, (preset: DataSharePreset) =>
        preset.source_fields.map(({ source_field_id }) => source_field_id),
      );
    }

    function getPresetOptionsPerFeed(
      presets: DataSharePreset[],
    ): Record<number, PresetOptionsGroup[]> {
      const options: Record<number, PresetOptionsGroup[]> = {};

      presets.forEach((preset: DataSharePreset) => {
        if (preset.preset_type === 'single_share_preset') return;

        // base_source_id is only filled if the preset is csv/gsheet type
        const parentId = preset.base_source_id ?? preset.feed_id;
        if (!options[parentId]) {
          options[parentId] = [
            {
              heading: null,
              items: [],
            },
          ];
        }

        const isDefault = preset.preset_type === 'crossbeam_default';
        const groupIndex = isDefault ? 0 : 1;

        if (!isDefault && options[parentId].length === 1) {
          options[parentId].splice(1, 0, {
            heading: null,
            items: [],
          });
        }

        options[parentId]?.[groupIndex]?.items.push({
          value: preset.id,
          label: getPresetName(
            preset.feed_id,
            preset.base_source_id || undefined,
            preset.id,
          ),
          presetType: preset.preset_type ?? undefined,
        });
      });

      return options;
    }

    function convertPresetsToOptions(
      presets: DataSharePreset[],
      feedId: number,
      sourceId?: number,
    ): PresetOptionsGroup[] {
      const options: PresetOptionsGroup[] = [
        {
          heading: null,
          items: [],
        },
      ];

      presets.forEach((preset) => {
        if (preset.preset_type === 'single_share_preset') return;

        const isDefault = preset.preset_type === 'crossbeam_default';
        const groupIndex = isDefault ? 0 : 1;

        if (!isDefault && options.length === 1) {
          options.push({
            heading: null,
            items: [],
          });
        }

        options[groupIndex]?.items.push({
          value: preset.id,
          label: getPresetName(feedId, sourceId, preset.id),
          presetType: preset.preset_type ?? undefined,
        });
      });

      return options;
    }

    refresh({ useCache: true });

    return {
      ready,
      readySync,
      allPresets,
      getPresetName,
      getDefaultPresetId,
      getFilteredPresetsByFeeId,
      getSourceFieldIdsByPresetId,
      getPresetOptionsPerFeed,
      convertPresetsToOptions,
      refreshDataSharePresetsStore: refresh,
    };
  },
);
